import { BrowserRouter, useLocation, useNavigate } from "react-router-dom";
import { StoreProvider } from "../app/providers/StoreProvider";
import { TelegramProvider } from "./providers/TelegramProvider";
import { AuthProvider } from "../app/providers/AuthProvider";
import { AppRouter } from "./router";
import { useEffect, useRef } from "react";
import NavMenu from "../components/NavMenu/NavMenu";
import LogoTop from "../components/mlogotop/LogoTop";
import type { WebApp } from "../types/telegram";
import NotificationWrapper from "../components/Notifications/NotificationWrapper";
import { useCartStore } from "../store/useCartStore";
import { useTelegram } from "./providers/TelegramProvider";

interface ExtendedWebApp extends WebApp {
  contentSafeAreaInset?: {
    top: number;
    bottom: number;
    left: number;
    right: number;
  };
}

const AppContent: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { updateCartCount } = useCartStore();
  const { launchParams } = useTelegram();
  const hasHandledStartParamRef = useRef(false);
  const isBannerPage = location.pathname.startsWith("/banner/");

  // Проверяем доступ на запись при запуске приложения
  // useEffect(() => {
  //   checkWriteAccessOnLoad().catch((error) => {
  //     console.error('Ошибка при проверке доступа на запись:', error);
  //   });
  // }, []);

  // Загружаем корзину при монтировании компонента
  useEffect(() => {
    const fetchCart = async () => {
      try {
        await updateCartCount();
      } catch (error) {
        console.error("Ошибка при загрузке корзины:", error);
      }
    };
    fetchCart();
  }, [updateCartCount]);

  // Устанавливаем CSS переменную для safe area insets глобально для всего приложения
  useEffect(() => {
    const tg = window.Telegram?.WebApp as ExtendedWebApp;
    if (!tg) return;

    const apply = () => {
      const top = tg?.contentSafeAreaInset?.top ?? 0;
      document.documentElement.style.setProperty(
        "--tg-viewport-content-safe-area-inset-top",
        `${top}px`,
      );
    };

    apply();
    tg.onEvent?.("contentSafeAreaChanged", apply);
    return () => {
      tg.offEvent?.("contentSafeAreaChanged", apply);
      // НЕ удаляем CSS переменную - она должна остаться для всех страниц
    };
  }, []);

  useEffect(() => {
    // Проверяем, является ли текущий путь главной страницей
    const isMainPage =
      location.pathname === "/" ||
      location.pathname === "/index.html" ||
      (!["/catalog", "/saved", "/bucket"].includes(location.pathname) &&
        !location.pathname.startsWith("/banner/"));

    if (isMainPage) {
      document.body.classList.add("main-page");
      document.body.classList.remove("tasks-page");
    } else if (location.pathname === "/tasks") {
      document.body.classList.add("tasks-page");
      document.body.classList.remove("main-page");
    } else {
      document.body.classList.remove("main-page", "tasks-page");
    }
  }, [location.pathname]);

  // deep-link
  useEffect(() => {
    if (hasHandledStartParamRef.current) return;

    const startParam = (launchParams as unknown as { start_param?: string })
      ?.start_param;
    if (!startParam) return;

    const match = startParam.match(/^open_product-(\d+)$/);
    if (!match) return;

    const productId = Number(match[1]);
    if (!Number.isFinite(productId)) return;

    navigate("/catalog", {
      replace: true,
      state: { openProductId: productId },
    });
    hasHandledStartParamRef.current = true;
  }, [launchParams, navigate]);

  return (
    <>
      <AppRouter />
      <NavMenu />
      {!isBannerPage && <LogoTop />}
    </>
  );
};

const AppEntry: React.FC = () => {
  const location = useLocation();
  const normalizedPath = location.pathname.replace(/\/+$/, "") || "/";

  if (normalizedPath === "/reviews") {
    return <AppRouter />;
  }

  return (
    <TelegramProvider>
      <StoreProvider>
        <AuthProvider>
          <AppContent />
          <NotificationWrapper />
        </AuthProvider>
      </StoreProvider>
    </TelegramProvider>
  );
};

const App: React.FC = () => (
  <BrowserRouter>
    <AppEntry />
  </BrowserRouter>
);

export default App;
